// src/components/ChatComponent.tsx
import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../security/AuthLogin";
import {
  fetchSoporteMensajes,
  sendSoporteMensaje,
  subirArchivoSoporte,
  fetchArchivosSoporte,
} from "../api/soportesApi";
import { getAuthToken } from "../security/AuthLogin";
import { io, Socket } from "socket.io-client";
import DOMPurify from "dompurify";
import { useDropzone } from "react-dropzone";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// ---------------------
// INTERFACES
// ---------------------
interface MensajeSoporte {
  id_soporte_detalle: number;
  id_soporte: number;
  id_usuario: string;
  f_respuesta: string;
  hora_respuesta: string;
  texto: string;
  es_cliente: boolean;
}

interface ArchivoSoporte {
  path: string;
  url?: string; // enlace firmado para abrir el archivo
  uploaderRole: "cliente" | "admin" | "tecnico";
  dateFolder?: string | null;
}

interface ChatComponentProps {
  idSoporte: number;
  clienteNombre?: string;
  clienteEmail?: string;
  clienteProfilePic?: string | null;
  sendText?: boolean;
  allowFileUpload?: boolean;
  showTypingIndicator?: boolean;
  onMessagesLoad?: (messages: MensajeSoporte[]) => void;
  onFilesLoad?: (files: ArchivoSoporte[]) => void;
}

// ---------------------
// CONSTANTES / HELPERS
// ---------------------
const BASE_URL = `${window.location.protocol}//${window.location.hostname}`;

function getFileNameFromUrl(url: string | undefined): string {
  if (!url) return "Archivo sin nombre";
  return url.substring(url.lastIndexOf("/") + 1);
}

function formatDateTime(f_respuesta: string, hora_respuesta: string): string {
  if (!f_respuesta || !hora_respuesta) return "Sin fecha";
  const f = f_respuesta.trim();
  const h = hora_respuesta.trim();
  const datePart = f.includes("T") ? f.split("T")[0] : f;
  const dateTimeString = `${datePart}T${h}`;
  const date = new Date(dateTimeString);
  if (isNaN(date.getTime())) return "Fecha inválida";
  const formatter = new Intl.DateTimeFormat("es-ES", {
    timeZone: "Europe/Madrid",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  return formatter.format(date);
}

// ---------------------
// COMPONENTE PRINCIPAL
// ---------------------
const ChatComponent: React.FC<ChatComponentProps> = ({
  idSoporte,
  clienteNombre,
  clienteEmail,
  clienteProfilePic,
  sendText = true,
  allowFileUpload = true,
  showTypingIndicator = true,
  onMessagesLoad,
  onFilesLoad,
}) => {
  const { userRole, userName, userData } = useAuth();

  const [mensajes, setMensajes] = useState<MensajeSoporte[]>([]);
  const [archivos, setArchivos] = useState<ArchivoSoporte[]>([]);
  const [nuevoMensaje, setNuevoMensaje] = useState("");
  const [archivoSubiendo, setArchivoSubiendo] = useState(false);
  const [archivoError, setArchivoError] = useState<string | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [alguienTyping, setAlguienTyping] = useState({
    isTyping: false,
    quien: "",
  });

  // ⭐ NUEVO: Estados para edición de mensajes (solo ADMIN)
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState("");

  // Estados para modal de confirmación de subida
  const [showUploadConfirmation, setShowUploadConfirmation] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<
    "pending" | "uploading" | "success" | "error"
  >("pending");

  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);

  // ---------------------
  // CONEXIÓN AL SOCKET
  // ---------------------
  useEffect(() => {
    const s = io(BASE_URL, {
      withCredentials: true,
      auth: (cb) => cb({ token: localStorage.getItem("authToken") }),
    });
    setSocket(s);
    return () => {
      s.disconnect();
    };
  }, []);

  // ---------------------
  // CARGA INICIAL
  // ---------------------
  useEffect(() => {
    if (idSoporte) {
      loadChat();
      if (allowFileUpload) {
        loadArchivos();
      }
    }
  }, [idSoporte, allowFileUpload]);

  const loadChat = async () => {
    try {
      const resp = await fetchSoporteMensajes(idSoporte);
      if (resp.success) {
        const mensajesInvertidos = [...resp.messages].reverse();
        setMensajes(mensajesInvertidos);
        onMessagesLoad?.(mensajesInvertidos);
      }
    } catch (error: any) {
      console.error("Error loading chat:", error);
    }
  };

  const loadArchivos = async () => {
    try {
      const resp = await fetchArchivosSoporte(idSoporte);
      if (resp.success) {
        setArchivos(resp.archivos);
        onFilesLoad?.(resp.archivos);
      }
    } catch (error: any) {
      console.error("Error loading files:", error);
    }
  };

  // ---------------------
  // SOCKET: ESCUCHAR EVENTOS
  // ---------------------
  useEffect(() => {
    if (socket && idSoporte) {
      socket.emit("joinSoporte", idSoporte);
      socket.on("nuevoMensaje", (mensaje: MensajeSoporte) => {
        setMensajes((prev) => {
          const newMessages = [mensaje, ...prev];
          onMessagesLoad?.(newMessages);
          return newMessages;
        });
        setTimeout(() => scrollToTop(), 100);
      });

      // ⭐ NUEVO: Escuchar eventos de edición y eliminación
      socket.on(
        "mensajeEditado",
        ({
          id_soporte_detalle,
          nuevoTexto,
        }: {
          id_soporte_detalle: number;
          nuevoTexto: string;
        }) => {
          setMensajes((prev) => {
            const updated = prev.map((msg) =>
              msg.id_soporte_detalle === id_soporte_detalle
                ? { ...msg, texto: nuevoTexto }
                : msg
            );
            onMessagesLoad?.(updated);
            return updated;
          });
        }
      );

      socket.on(
        "mensajeEliminado",
        ({ id_soporte_detalle }: { id_soporte_detalle: number }) => {
          setMensajes((prev) => {
            const filtered = prev.filter(
              (msg) => msg.id_soporte_detalle !== id_soporte_detalle
            );
            onMessagesLoad?.(filtered);
            return filtered;
          });
        }
      );

      if (showTypingIndicator) {
        socket.on("alguienEstaEscribiendo", ({ isTyping, quien }) => {
          setAlguienTyping({ isTyping, quien });
          if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
          if (isTyping) {
            typingTimeoutRef.current = setTimeout(() => {
              setAlguienTyping({ isTyping: false, quien: "" });
            }, 5000);
          }
        });
      }
    }
    return () => {
      if (socket) {
        socket.off("nuevoMensaje");
        socket.off("mensajeEditado");
        socket.off("mensajeEliminado");
        socket.off("alguienEstaEscribiendo");
      }
    };
  }, [socket, idSoporte, showTypingIndicator, onMessagesLoad]);

  const scrollToTop = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  // ---------------------
  // ⭐ NUEVAS FUNCIONES ADMIN: EDITAR Y ELIMINAR MENSAJES
  // ---------------------
  const handleEditMessage = (messageId: number, currentText: string) => {
    if (userRole !== "admin") return;
    setEditingMessageId(messageId);
    setEditingText(currentText);
  };

  const handleSaveEdit = async () => {
    if (!editingMessageId || !editingText.trim() || userRole !== "admin")
      return;

    try {
      const response = await fetch(
        `${BASE_URL}/api/soportes/${idSoporte}/mensajes/${editingMessageId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({ texto: editingText.trim() }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error al editar mensaje: ${errorText}`);
      }

      const result = await response.json();
      if (result.success) {
        // El mensaje se actualizará automáticamente vía socket
        setEditingMessageId(null);
        setEditingText("");
      } else {
        alert(`Error: ${result.message}`);
      }
    } catch (error: any) {
      alert(`Error al editar mensaje: ${error.message}`);
      console.error(error);
    }
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditingText("");
  };

  const handleDeleteMessage = async (messageId: number) => {
    if (userRole !== "admin") return;

    const confirmDelete = window.confirm(
      "¿Estás seguro de que deseas eliminar este mensaje? Esta acción no se puede deshacer."
    );
    if (!confirmDelete) return;

    try {
      const response = await fetch(
        `${BASE_URL}/api/soportes/${idSoporte}/mensajes/${messageId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error al eliminar mensaje: ${errorText}`);
      }

      const result = await response.json();
      if (result.success) {
        // El mensaje se eliminará automáticamente vía socket
      } else {
        alert(`Error: ${result.message}`);
      }
    } catch (error: any) {
      alert(`Error al eliminar mensaje: ${error.message}`);
      console.error(error);
    }
  };

  // ---------------------
  // CHAT: typing y envío
  // ---------------------
  const handleTyping = () => {
    if (!socket || !idSoporte || !showTypingIndicator) return;
    socket.emit("typing", {
      soporteId: idSoporte,
      isTyping: true,
      quien: userName || "Usuario",
    });
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit("typing", {
        soporteId: idSoporte,
        isTyping: false,
        quien: userName || "Usuario",
      });
    }, 5000);
  };

  const handleSend = async () => {
    if (!nuevoMensaje.trim() || !idSoporte) return;
    const textoAEnviar = DOMPurify.sanitize(nuevoMensaje.trim(), {
      ALLOWED_TAGS: [
        "b",
        "i",
        "u",
        "strike",
        "blockquote",
        "a",
        "img",
        "p",
        "ul",
        "ol",
        "li",
        "strong",
        "em",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "br",
        "hr",
        "span",
        "div",
      ],
      ALLOWED_ATTR: ["href", "src", "alt", "title", "style", "class"],
    });
    setNuevoMensaje("");
    try {
      await sendSoporteMensaje(idSoporte, textoAEnviar);
      if (socket && showTypingIndicator) {
        socket.emit("typing", {
          soporteId: idSoporte,
          isTyping: false,
          quien: userName || "Usuario",
        });
      }
      setTimeout(() => scrollToTop(), 50);
    } catch (error: any) {
      alert(`Error al enviar mensaje: ${error.message}`);
      console.error(error);
    }
  };

  // Función para manejar la confirmación de subida
  const handleUploadConfirmation = async (confirmed: boolean) => {
    if (confirmed && pendingFiles.length > 0) {
      setUploadStatus("uploading");
      setArchivoSubiendo(true);
      setUploadProgress(0);

      try {
        const totalFiles = pendingFiles.length;
        for (let i = 0; i < pendingFiles.length; i++) {
          const file = pendingFiles[i];
          await subirArchivoSoporte(idSoporte, file);

          const progress = ((i + 1) / totalFiles) * 100;
          setUploadProgress(progress);

          await new Promise((resolve) => setTimeout(resolve, 200));
        }

        setUploadStatus("success");
        await loadArchivos();

        setTimeout(() => {
          handleUploadReset();
        }, 1500);
      } catch (error: any) {
        setUploadStatus("error");
        setArchivoError(
          error instanceof Error ? error.message : "Error desconocido"
        );
      } finally {
        setArchivoSubiendo(false);
      }
    } else {
      handleUploadReset();
    }
  };

  const handleUploadReset = () => {
    setShowUploadConfirmation(false);
    setPendingFiles([]);
    setArchivoError(null);
    setUploadProgress(0);
    setUploadStatus("pending");
  };

  // ---------------------
  // DROPZONE (subir archivos)
  // ---------------------
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: async (acceptedFiles: File[]) => {
      if (!idSoporte || !allowFileUpload) return;

      if (userRole === "cliente") {
        setPendingFiles(acceptedFiles);
        setShowUploadConfirmation(true);
        return;
      }

      setArchivoSubiendo(true);
      try {
        for (const file of acceptedFiles) {
          await subirArchivoSoporte(idSoporte, file);
        }
        await loadArchivos();
        alert("Archivos subidos exitosamente");
      } catch (error: any) {
        setArchivoError(
          error instanceof Error ? error.message : "Error desconocido"
        );
      } finally {
        setArchivoSubiendo(false);
      }
    },
    multiple: true,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".gif"],
      "application/pdf": [".pdf"],
      "application/msword": [".doc", ".docx"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/rtf": [".rtf"],
      "text/rtf": [".rtf"],
      "application/xml": [".xml"],
      "text/xml": [".xml"],
      "application/zip": [".zip"],
      "application/x-zip-compressed": [".zip"],
      "application/vnd.rar": [".rar"],
      "application/x-rar-compressed": [".rar"],
    },
    disabled: !allowFileUpload,
  });

  const handleDeleteArchivo = async (
    filePath: string,
    uploaderRole: "cliente" | "admin" | "tecnico"
  ) => {
    if (!idSoporte) return;

    const canDelete =
      userRole === "admin" ||
      (userRole === "tecnico" && uploaderRole === "tecnico");

    if (!canDelete) {
      alert("No tienes permiso para eliminar este archivo.");
      return;
    }

    const confirmDelete = window.confirm(
      "¿Estás seguro de que deseas eliminar este archivo?"
    );
    if (!confirmDelete) return;

    try {
      const resp = await fetch(
        `${BASE_URL}/api/soportes/${idSoporte}/archivos`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({ path: filePath }),
        }
      );
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`Error al eliminar archivo: ${txt}`);
      }
      const data = await resp.json();
      if (data.success) {
        await loadArchivos();
        alert("Archivo eliminado exitosamente.");
      } else {
        alert(`Error: ${data.message}`);
      }
    } catch (error: any) {
      alert(`Error al eliminar archivo: ${error.message}`);
      console.error(error);
    }
  };

  // ---------------------
  // RENDER
  // ---------------------
  const esAdminOTec = userRole === "admin" || userRole === "tecnico";
  const esAdmin = userRole === "admin";

  return (
    <div className="main-container">
      {/* Modal de confirmación para subida de archivos */}
      {showUploadConfirmation && (
        <div className="upload-modal-overlay">
          <div className="upload-modal-content">
            {uploadStatus === "pending" && (
              <>
                <div className="upload-modal-header">
                  <h3>Confirmar subida de archivo(s)</h3>
                </div>
                <div className="upload-modal-body">
                  <p>
                    Vas a subir <strong>{pendingFiles.length}</strong>{" "}
                    archivo(s):
                  </p>
                  <ul className="files-list">
                    {pendingFiles.map((file, index) => (
                      <li key={index} className="file-item-modal">
                        <span className="file-icon">📄</span>
                        <div className="file-info-modal">
                          <span className="file-name">{file.name}</span>
                          <span className="file-size">
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <div className="upload-warning">
                    <span className="warning-icon">⚠️</span>
                    Una vez subidos, <strong>no podrás eliminarlos</strong>.
                    ¿Deseas continuar?
                  </div>
                </div>
                <div className="upload-modal-buttons">
                  <button
                    onClick={() => handleUploadConfirmation(true)}
                    className="btn-upload-confirm"
                  >
                    Sí, subir archivos
                  </button>
                  <button
                    onClick={() => handleUploadConfirmation(false)}
                    className="btn-upload-cancel"
                  >
                    Cancelar
                  </button>
                </div>
              </>
            )}

            {uploadStatus === "uploading" && (
              <>
                <div className="upload-modal-header">
                  <h3>Subiendo archivos...</h3>
                </div>
                <div className="upload-modal-body">
                  <div className="upload-progress-container">
                    <div className="upload-progress-bar">
                      <div
                        className="upload-progress-fill"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <div className="upload-progress-text">
                      {Math.round(uploadProgress)}% completado
                    </div>
                  </div>
                  <p className="upload-status-text">
                    Subiendo {pendingFiles.length} archivo(s)...
                  </p>
                </div>
              </>
            )}

            {uploadStatus === "success" && (
              <>
                <div className="upload-modal-header success">
                  <h3>¡Archivos subidos exitosamente! ✅</h3>
                </div>
                <div className="upload-modal-body">
                  <div className="success-animation">
                    <div className="checkmark">✓</div>
                  </div>
                  <p className="success-text">
                    Se han subido <strong>{pendingFiles.length}</strong>{" "}
                    archivo(s) correctamente.
                  </p>
                </div>
              </>
            )}

            {uploadStatus === "error" && (
              <>
                <div className="upload-modal-header error">
                  <h3>Error al subir archivos ❌</h3>
                </div>
                <div className="upload-modal-body">
                  <div className="error-message-modal">{archivoError}</div>
                  <div className="upload-modal-buttons">
                    <button
                      onClick={() => handleUploadReset()}
                      className="btn-upload-cancel"
                    >
                      Cerrar
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="chat-container">
        {esAdminOTec && showTypingIndicator && alguienTyping.isTyping && (
          <div className="typing-indicator">
            {alguienTyping.quien} está escribiendo...
          </div>
        )}
        <div className="chat-messages" ref={chatContainerRef}>
          {mensajes.length === 0 ? (
            <p>No hay mensajes</p>
          ) : (
            mensajes.map((m) => {
              const fechaFormateada = formatDateTime(
                m.f_respuesta,
                m.hora_respuesta
              );
              const isFromCurrentUser =
                (m.es_cliente && userRole === "cliente") ||
                (!m.es_cliente &&
                  (userRole === "admin" || userRole === "tecnico"));
              const bubbleClass = isFromCurrentUser ? "from-me" : "from-other";

              let imagenUrl = "";
              if (isFromCurrentUser) {
                if (userRole === "cliente") {
                  imagenUrl = clienteProfilePic
                    ? `${BASE_URL}${clienteProfilePic}`
                    : "/images/default-user.png";
                } else {
                  imagenUrl = userData?.profilepic
                    ? `${BASE_URL}${userData.profilepic}`
                    : "/images/oclem-logo.png";
                }
              } else {
                if (m.es_cliente) {
                  imagenUrl = clienteProfilePic
                    ? `${BASE_URL}${clienteProfilePic}`
                    : "/images/default-user.png";
                } else {
                  imagenUrl = "/images/admin-profile.png";
                }
              }

              const sanitizedHTML = DOMPurify.sanitize(m.texto || "", {
                ALLOWED_TAGS: [
                  "b",
                  "i",
                  "u",
                  "strike",
                  "blockquote",
                  "a",
                  "img",
                  "p",
                  "ul",
                  "ol",
                  "li",
                  "strong",
                  "em",
                  "h1",
                  "h2",
                  "h3",
                  "h4",
                  "h5",
                  "h6",
                  "br",
                  "hr",
                  "span",
                  "div",
                ],
                ALLOWED_ATTR: ["href", "src", "alt", "title", "style"],
              });
              const fixedHTML = sanitizedHTML.replace(
                /href="[^"]*~(https?:\/\/[^~]+)~"/g,
                'href="$1"'
              );
              const finalHTML = fixedHTML.replace(
                /<a(?![^>]*target)/g,
                '<a target="_blank"'
              );

              const nombreParaMostrar = m.es_cliente
                ? clienteNombre || "Cliente"
                : "Grupo Oclem";
              const infoTop = `${fechaFormateada} | ${nombreParaMostrar}`;

              return (
                <div
                  key={m.id_soporte_detalle}
                  className={`message-bubble ${bubbleClass}`}
                >
                  <img src={imagenUrl} alt="Profile" className="profile-pic" />
                  <div className="message-content">
                    <div className="message-info">
                      {infoTop}
                      {/* ⭐ NUEVO: Botones de editar y eliminar solo para ADMIN */}
                      {esAdmin && (
                        <div className="admin-message-actions">
                          <button
                            onClick={() =>
                              handleEditMessage(m.id_soporte_detalle, m.texto)
                            }
                            className="btn-edit-message"
                            title="Editar mensaje"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() =>
                              handleDeleteMessage(m.id_soporte_detalle)
                            }
                            className="btn-delete-message"
                            title="Eliminar mensaje"
                          >
                            🗑️
                          </button>
                        </div>
                      )}
                    </div>

                    {/* ⭐ NUEVO: Modo de edición */}
                    {editingMessageId === m.id_soporte_detalle ? (
                      <div className="edit-message-container">
                        <ReactQuill
                          theme="snow"
                          value={editingText}
                          onChange={setEditingText}
                          placeholder="Editar mensaje..."
                          className="edit-message-input"
                          style={{ translate: "no" }}
                          modules={{
                            toolbar: [
                              [{ header: [1, 2, false] }],
                              [
                                "bold",
                                "italic",
                                "underline",
                                "strike",
                                "blockquote",
                              ],
                              [
                                { list: "ordered" },
                                { list: "bullet" },
                                { indent: "-1" },
                                { indent: "+1" },
                              ],
                              ["link", "image"],
                              ["clean"],
                            ],
                          }}
                          formats={[
                            "header",
                            "bold",
                            "italic",
                            "underline",
                            "strike",
                            "blockquote",
                            "list",
                            "bullet",
                            "indent",
                            "link",
                            "image",
                          ]}
                        />
                        <div className="edit-message-buttons">
                          <button
                            onClick={handleSaveEdit}
                            disabled={!editingText.trim()}
                            className="btn btn-guardar btn-small"
                          >
                            Guardar
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="btn btn-cancelar btn-small"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div dangerouslySetInnerHTML={{ __html: finalHTML }} />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {sendText && (
          <div className="send-message-container" translate="no">
            <ReactQuill
              theme="snow"
              value={nuevoMensaje}
              onChange={(content) => {
                setNuevoMensaje(content);
                handleTyping();
              }}
              placeholder="Escribe tu mensaje..."
              className="message-input"
              style={{ translate: "no" }}
              modules={{
                toolbar: [
                  [{ header: [1, 2, false] }],
                  ["bold", "italic", "underline", "strike", "blockquote"],
                  [
                    { list: "ordered" },
                    { list: "bullet" },
                    { indent: "-1" },
                    { indent: "+1" },
                  ],
                  ["link", "image"],
                  ["clean"],
                ],
              }}
              formats={[
                "header",
                "bold",
                "italic",
                "underline",
                "strike",
                "blockquote",
                "list",
                "bullet",
                "indent",
                "link",
                "image",
              ]}
            />
            <button
              onClick={handleSend}
              disabled={!nuevoMensaje.trim()}
              className="btn btn-guardar"
            >
              Enviar
            </button>
          </div>
        )}
      </div>

      {allowFileUpload && (
        <div className="archivos-container">
          <h3>Archivos</h3>
          <div
            {...getRootProps()}
            className={`dropzone ${isDragActive ? "active" : ""}`}
          >
            <input {...getInputProps()} />
            {isDragActive ? (
              <p>Suelta los archivos aquí...</p>
            ) : (
              <p>
                Arrastra y suelta archivos aquí, o haz clic para seleccionar
                archivos
                {userRole === "cliente" && (
                  <>
                    <br />
                    <small style={{ color: "#666", fontSize: "0.9em" }}>
                      Nota: Una vez subidos, no podrás eliminar los archivos
                    </small>
                  </>
                )}
              </p>
            )}
          </div>
          {archivoSubiendo && <p>Subiendo archivo...</p>}
          {archivoError && (
            <p className="error-message">Error: {archivoError}</p>
          )}
          <div className="archivos-list">
            {archivos.length === 0 ? (
              <p>No hay archivos subidos.</p>
            ) : (
              <div className="files-container">
                {/* Columna: Cliente */}
                <div className="files-column">
                  <h4>Cliente</h4>
                  {archivos
                    .filter((file) => file.uploaderRole === "cliente")
                    .map((archivo, index) => {
                      const { path, url, dateFolder } = archivo;
                      let formattedDate = "Sin fecha";
                      if (dateFolder) {
                        const parts = dateFolder.split("-");
                        if (parts.length === 3) {
                          const [yyyy, mm, dd] = parts;
                          formattedDate = `${dd}/${mm}/${yyyy}`;
                        } else {
                          formattedDate = dateFolder;
                        }
                      }

                      const canDelete =
                        userRole === "admin" ||
                        (userRole === "tecnico" &&
                          archivo.uploaderRole === "tecnico");

                      const fileName = getFileNameFromUrl(path);
                      return (
                        <div key={index} className="file-item">
                          <div className="file-info">
                            <img
                              src={
                                clienteProfilePic
                                  ? `${BASE_URL}${clienteProfilePic}`
                                  : "/images/default-user.png"
                              }
                              alt="Cliente"
                              className="file-profile-pic"
                            />
                            <div className="file-details">
                              <span className="uploader-label">
                                {userRole === "admin"
                                  ? clienteNombre || "Cliente"
                                  : "Tú"}
                                :
                              </span>
                              <a
                                href={`${BASE_URL}${url ?? path}`}
                                target="_blank"
                                rel="noreferrer"
                                className="file-link"
                              >
                                {fileName}
                              </a>
                              <span className="file-timestamp">
                                {formattedDate}
                              </span>
                            </div>
                          </div>
                          {canDelete && (
                            <button
                              className="btn btn-eliminar"
                              onClick={() =>
                                handleDeleteArchivo(path, "cliente")
                              }
                            >
                              Eliminar
                            </button>
                          )}
                        </div>
                      );
                    })}
                </div>
                {/* Columna: Grupo Oclem */}
                <div className="files-column">
                  <h4>Grupo Oclem</h4>
                  {archivos
                    .filter(
                      (file) =>
                        file.uploaderRole === "admin" ||
                        file.uploaderRole === "tecnico"
                    )
                    .map((archivo, index) => {
                      const { path, url, dateFolder, uploaderRole } = archivo;
                      let formattedDate = "Sin fecha";
                      if (dateFolder) {
                        const parts = dateFolder.split("-");
                        if (parts.length === 3) {
                          const [yyyy, mm, dd] = parts;
                          formattedDate = `${dd}/${mm}/${yyyy}`;
                        } else {
                          formattedDate = dateFolder;
                        }
                      }

                      let uploaderLabel = "";
                      let uploaderImageUrl = "";
                      if (uploaderRole === "admin") {
                        uploaderLabel = "Grupo Oclem";
                        uploaderImageUrl =
                          userRole === "cliente"
                            ? "/images/admin-profile.png"
                            : userData?.profilepic
                            ? `${BASE_URL}${userData.profilepic}`
                            : "/images/oclem-logo.png";
                      } else if (uploaderRole === "tecnico") {
                        uploaderLabel = "Grupo Oclem";
                        uploaderImageUrl =
                          userRole === "cliente"
                            ? "/images/tecnico-profile.png"
                            : userData?.profilepic
                            ? `${BASE_URL}${userData.profilepic}`
                            : "/images/default-user.png";
                      }

                      const canDelete =
                        userRole === "admin" ||
                        (userRole === "tecnico" && uploaderRole === "tecnico");

                      const fileName = getFileNameFromUrl(path);
                      return (
                        <div key={index} className="file-item">
                          <div className="file-info">
                            <img
                              src={uploaderImageUrl}
                              alt={uploaderLabel}
                              className="file-profile-pic"
                            />
                            <div className="file-details">
                              <span className="uploader-label">
                                {uploaderLabel}:
                              </span>
                              <a
                                href={`${BASE_URL}${url ?? path}`}
                                target="_blank"
                                rel="noreferrer"
                                className="file-link"
                              >
                                {fileName}
                              </a>
                              <span className="file-timestamp">
                                {formattedDate}
                              </span>
                            </div>
                          </div>
                          {canDelete && (
                            <button
                              className="btn btn-eliminar"
                              onClick={() =>
                                handleDeleteArchivo(path, uploaderRole)
                              }
                            >
                              Eliminar
                            </button>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatComponent;
